import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { Button, Icon } from '@anytag/shared-atoms';
import { AddTagModal, DeleteTagModal } from '@anytag/shared-molecules';
import { cn } from '@anytag/shared-utils';
import { AnalyticsHashtagType } from '../types';
import { useManageHashtags } from './hooks';

export interface AnalyticsHashtagsListProps<T extends AnalyticsHashtagType = AnalyticsHashtagType> {
  hashtags: T[];
  adding?: boolean;
  deleting?: boolean;
  maxHashtags?: number;
  activeHashtag?: T['id'];
  onSelectHashtag?: (hashtag?: T) => void;
  onDeleteHashtag?: (hashtag: T, cb?: () => void) => void;
  onAddNewHashtag?: (hashtag: string, cb?: () => void) => void;
}

export const AnalyticsHashtagsList = <T extends AnalyticsHashtagType>({
  adding,
  hashtags,
  deleting,
  activeHashtag,
  maxHashtags = 5,
  onSelectHashtag,
  onAddNewHashtag,
  onDeleteHashtag
}: AnalyticsHashtagsListProps<T>) => {
  const { t } = useTranslation();
  const {
    addModalState,
    deletingHashtag,
    setDeletingHashtag,
    handleDeleteHashtag,
    handleCreateHashtag,
    resetDeletingHashtag
  } = useManageHashtags({ onAddNewHashtag, onDeleteHashtag });

  const disabledAddButton = hashtags.length > maxHashtags;

  return (
    <>
      <div css={styles.wrapper}>
        <div css={styles.itemWrapper}>
          <Button
            title="+"
            variant="outline"
            className={styles.item}
            onClick={addModalState.open}
            disabled={disabledAddButton}
            tooltip={{
              disabled: false,
              help: disabledAddButton ? t('Can’t add tags more than', { count: maxHashtags }) : t('Tooltip.Add Hashtag')
            }}
          />
        </div>

        {hashtags.map((hashtag) => {
          const active = hashtag.id === activeHashtag;

          return (
            <div key={hashtag.id} css={styles.itemWrapper}>
              <Button
                disabled={hashtag.blocked}
                variant={active ? 'solid' : 'outline'}
                className={cn(styles.item, 'gap-1 pr-3 pl-4')}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectHashtag?.(active ? undefined : hashtag);
                }}
              >
                <p className={cn('flex-1 tracking-[0.5px]', active ? 'font-semibold' : 'font-normal')}>
                  {hashtag.name}
                </p>

                {!hashtag.blocked ? (
                  <div
                    css={styles.icon}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeletingHashtag(hashtag);
                    }}
                  >
                    <Icon size={8} icon="close" variant="inherit" />
                  </div>
                ) : null}
              </Button>
            </div>
          );
        })}
      </div>

      <AddTagModal
        loading={adding}
        open={addModalState.status}
        onAdd={handleCreateHashtag}
        onClose={addModalState.close}
      />

      <DeleteTagModal
        loading={deleting}
        tag={deletingHashtag?.name}
        open={!!deletingHashtag?.id}
        onClose={resetDeletingHashtag}
        onDelete={handleDeleteHashtag}
      />
    </>
  );
};
const styles = {
  wrapper: css({
    margin: '-4px',
    display: 'flex',
    flexWrap: 'wrap',
    boxSizing: 'border-box'
  }),
  itemWrapper: css({
    padding: '4px',
    boxSizing: 'border-box'
  }),
  item: cn('min-w-30 px-3'),
  icon: css({
    flex: 'none',
    width: '12px',
    height: '12px',
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'center',
    justifyContent: 'center'
  })
};
